import React, { useCallback, useEffect, useRef, useState } from "react";
import { useI18n } from "../../lib/i18n";
import { ai, projects as projectsApi } from "../../lib/api";
import {
  PencilSimple, PaintBrush, Eraser, PaintBucket, Eyedropper, LineSegment, Rectangle,
  Circle, Selection, ArrowsOutCardinal, Gradient, Drop, Stamp, FlipHorizontal,
  FlipVertical, ArrowClockwise, ArrowCounterClockwise, Crop, ArrowUUpLeft, ArrowUUpRight,
  Trash, FloppyDisk, Download, Sparkle, Copy, Plus, MagicWand, TextT, DropHalf,
  ArrowsLeftRight, Path,
} from "@phosphor-icons/react";
import {
  proceduralSprite, dataUrlToPixelGrid, quantizeToPalette, assetFileName,
  scalePixelGrid, padPixelGrid, STANDARD_PIXEL_SIZES,
} from "../../lib/gamecraft";
import { toast } from "sonner";

const PALETTE = [
  "#000000", "#1f1f29", "#3d3d4a", "#7c7c8a", "#c8c8d0", "#ffffff",
  "#e63946", "#f1a208", "#ffd60a", "#06d6a0", "#118ab2", "#22d3ee",
  "#7c3aed", "#ec4899", "#84cc16", "#fb7185", "#f97316", "#a16207",
];
const CELL_SIZES = [6, 8, 10, 12, 14, 16, 20];
const TOOLS = [
  { id: "pencil", icon: PencilSimple, label: "Lápiz (P)" },
  { id: "brush", icon: PaintBrush, label: "Pincel (B)" },
  { id: "eraser", icon: Eraser, label: "Goma (E)" },
  { id: "bucket", icon: PaintBucket, label: "Bote (G)" },
  { id: "recolor", icon: Drop, label: "Recolor" },
  { id: "picker", icon: Eyedropper, label: "Cuentagotas (I)" },
  { id: "line", icon: LineSegment, label: "Línea (N)" },
  { id: "rect", icon: Rectangle, label: "Rectángulo (U)" },
  { id: "roundrect", icon: Path, label: "Rect. redondeado" },
  { id: "ellipse", icon: Circle, label: "Elipse (O)" },
  { id: "select", icon: Selection, label: "Selección (M)" },
  { id: "wand", icon: MagicWand, label: "Varita (W)" },
  { id: "move", icon: ArrowsOutCardinal, label: "Mover" },
  { id: "gradient", icon: Gradient, label: "Degradado (H)" },
  { id: "clone", icon: Stamp, label: "Clonar (S)" },
  { id: "tone", icon: DropHalf, label: "Aclarar / oscurecer (Alt)" },
  { id: "text", icon: TextT, label: "Texto (T)" },
];

const emptyGrid = (n) => Array(n * n).fill(null);

function hexRgb(h) {
  const n = parseInt(String(h || "#000000").replace("#", ""), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function rgbHex(r, g, b) {
  const h = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}
function lerpHex(a, b, t) {
  const A = hexRgb(a), B = hexRgb(b);
  return rgbHex(A[0] + (B[0] - A[0]) * t, A[1] + (B[1] - A[1]) * t, A[2] + (B[2] - A[2]) * t);
}
function colorDist(a, b) {
  if (a === b) return 0;
  if (!a || !b) return 999;
  const A = hexRgb(a), B = hexRgb(b);
  return Math.abs(A[0] - B[0]) + Math.abs(A[1] - B[1]) + Math.abs(A[2] - B[2]);
}
function toneHex(hex, lighter) {
  if (!hex) return hex;
  const [r, g, b] = hexRgb(hex);
  const k = lighter ? 40 : -40;
  return rgbHex(r + k, g + k, b + k);
}

function bresenham(x0, y0, x1, y1) {
  const pts = [];
  const dx = Math.abs(x1 - x0), dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
  let err = dx - dy, x = x0, y = y0;
  for (;;) {
    pts.push([x, y]);
    if (x === x1 && y === y1) break;
    const e2 = 2 * err;
    if (e2 > -dy) { err -= dy; x += sx; }
    if (e2 < dx) { err += dx; y += sy; }
  }
  return pts;
}

function stampAt(next, grid, cx, cy, color, size, erase = false) {
  const r = Math.max(0, Math.floor((size - 1) / 2));
  for (let y = cy - r; y <= cy + r; y++) {
    for (let x = cx - r; x <= cx + r; x++) {
      if (x < 0 || y < 0 || x >= grid || y >= grid) continue;
      if (r > 0 && (x - cx) ** 2 + (y - cy) ** 2 > r * r + 0.5) continue;
      next[y * grid + x] = erase ? null : color;
    }
  }
}

function rectPts(x0, y0, x1, y1, fill) {
  const a = Math.min(x0, x1), b = Math.max(x0, x1), c = Math.min(y0, y1), d = Math.max(y0, y1);
  const pts = [];
  for (let y = c; y <= d; y++) for (let x = a; x <= b; x++) {
    if (fill || x === a || x === b || y === c || y === d) pts.push([x, y]);
  }
  return pts;
}

function roundRectPts(x0, y0, x1, y1, fill) {
  const a = Math.min(x0, x1), b = Math.max(x0, x1), c = Math.min(y0, y1), d = Math.max(y0, y1);
  const r = Math.max(1, Math.min(3, Math.floor(Math.min(b - a, d - c) / 2)));
  const inside = (x, y) => {
    if (x < a || x > b || y < c || y > d) return false;
    const ix0 = a + r, ix1 = b - r, iy0 = c + r, iy1 = d - r;
    if (x >= ix0 && x <= ix1) return true;
    if (y >= iy0 && y <= iy1) return true;
    return [[ix0, iy0], [ix1, iy0], [ix0, iy1], [ix1, iy1]]
      .some(([cx, cy]) => (x - cx) ** 2 + (y - cy) ** 2 <= r * r + 0.35);
  };
  const pts = [];
  for (let y = c; y <= d; y++) for (let x = a; x <= b; x++) {
    if (!inside(x, y)) continue;
    if (fill || !inside(x - 1, y) || !inside(x + 1, y) || !inside(x, y - 1) || !inside(x, y + 1)) pts.push([x, y]);
  }
  return pts;
}

function ellipsePts(x0, y0, x1, y1, fill) {
  const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
  const rx = Math.max(0.5, Math.abs(x1 - x0) / 2), ry = Math.max(0.5, Math.abs(y1 - y0) / 2);
  const a = Math.min(x0, x1), b = Math.max(x0, x1), c = Math.min(y0, y1), d = Math.max(y0, y1);
  const pts = [];
  for (let y = c; y <= d; y++) for (let x = a; x <= b; x++) {
    const v = ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2;
    if (fill ? v <= 1.08 : v <= 1.08 && v >= 0.52) pts.push([x, y]);
  }
  return pts;
}

function floodFill(pixels, grid, start, color, tolerance = 0) {
  const target = pixels[start];
  if (target === color && !tolerance) return pixels;
  const next = [...pixels];
  const seen = new Uint8Array(pixels.length);
  const stack = [start];
  while (stack.length) {
    const idx = stack.pop();
    if (seen[idx]) continue;
    seen[idx] = 1;
    if (colorDist(next[idx], target) > tolerance * 12) continue;
    next[idx] = color;
    const x = idx % grid, y = Math.floor(idx / grid);
    if (x > 0) stack.push(idx - 1);
    if (x < grid - 1) stack.push(idx + 1);
    if (y > 0) stack.push(idx - grid);
    if (y < grid - 1) stack.push(idx + grid);
  }
  return next;
}

function floodIdx(pixels, grid, start, tolerance = 0) {
  const target = pixels[start];
  const out = [];
  const seen = new Uint8Array(pixels.length);
  const stack = [start];
  while (stack.length) {
    const idx = stack.pop();
    if (seen[idx]) continue;
    seen[idx] = 1;
    if (colorDist(pixels[idx], target) > tolerance * 12) continue;
    out.push(idx);
    const x = idx % grid, y = Math.floor(idx / grid);
    if (x > 0) stack.push(idx - 1);
    if (x < grid - 1) stack.push(idx + 1);
    if (y > 0) stack.push(idx - grid);
    if (y < grid - 1) stack.push(idx + grid);
  }
  return out;
}

function paintPtsGrid(base, grid, pts, color) {
  const next = [...base];
  pts.forEach(([x, y]) => {
    if (x >= 0 && y >= 0 && x < grid && y < grid) next[y * grid + x] = color;
  });
  return next;
}

function boundsOf(indices, grid) {
  if (!indices.length) return null;
  let x0 = 1e9, y0 = 1e9, x1 = 0, y1 = 0;
  indices.forEach((i) => {
    const x = i % grid, y = Math.floor(i / grid);
    if (x < x0) x0 = x; if (y < y0) y0 = y;
    if (x > x1) x1 = x; if (y > y1) y1 = y;
  });
  return { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

export default function SpriteEditorPanel({ project, onChange, studio, loadSprite, onLoaded }) {
  const { t } = useI18n();
  const kinds = studio?.kinds || [{ id: "sprite", label: "Sprite" }];
  const subtypes = studio?.subtypes || [];
  const canvasRef = useRef(null);
  const gridRef = useRef(studio?.size || 32);
  const pixelsRef = useRef([]);
  const sourceUrlRef = useRef(null);
  const dirtyRef = useRef(false);
  const strokeBase = useRef(null);
  const cloneAnchor = useRef(null);
  const clipRef = useRef(null);
  const [kind, setKind] = useState(kinds[0].id);
  const [subtype, setSubtype] = useState(subtypes[0]?.id || "");
  const [grid, setGrid] = useState(studio?.size || 32);
  const [customSize, setCustomSize] = useState("");
  const [scaleMode, setScaleMode] = useState("scale");
  const [cell, setCell] = useState(14);
  const [pixels, setPixels] = useState(() => emptyGrid(studio?.size || 32));
  const [color, setColor] = useState("#22d3ee");
  const [colorB, setColorB] = useState("#0f172a");
  const [tool, setTool] = useState("pencil");
  const [brush, setBrush] = useState(1);
  const [fillShape, setFillShape] = useState(false);
  const [tolerance, setTolerance] = useState(0);
  const [drawing, setDrawing] = useState(false);
  const [drag, setDrag] = useState(null);
  const [hover, setHover] = useState(null);
  const [sel, setSel] = useState(null);
  const [mask, setMask] = useState(null);
  const [cloneSrc, setCloneSrc] = useState(null);
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [name, setName] = useState("sprite-1");
  const [editId, setEditId] = useState(null);
  const lockedPalette = project.config?.palette || [];
  const camera = project.config?.camera === "topdown"
    ? "top-down Enter the Gungeon view"
    : "side view Metal Slug platformer";

  gridRef.current = grid;
  pixelsRef.current = pixels;

  useEffect(() => {
    setKind(kinds[0].id);
    setSubtype((studio?.subtypes || [])[0]?.id || "");
  }, [studio?.id]); // eslint-disable-line

  const pushHist = (prev) => {
    dirtyRef.current = true;
    setHistory((h) => [...h.slice(-40), prev]);
    setFuture([]);
  };

  const applyCells = async (url, size) => {
    let cells = await dataUrlToPixelGrid(url, size);
    if (lockedPalette.length) cells = quantizeToPalette(cells, lockedPalette);
    return cells;
  };

  const setGridKeep = (nextSize) => {
    const n = Math.max(4, Math.min(256, Number(nextSize) || 32));
    const from = gridRef.current;
    if (sourceUrlRef.current && !dirtyRef.current) {
      applyCells(sourceUrlRef.current, n).then((cells) => {
        setPixels(cells);
        setGrid(n);
        setSel(null);
        setMask(null);
      }).catch(() => {});
      return;
    }
    setPixels((prev) => (scaleMode === "pad" ? padPixelGrid(prev, from, n) : scalePixelGrid(prev, from, n)));
    setGrid(n);
    setSel(null);
    setMask(null);
  };

  useEffect(() => {
    if (!loadSprite?.data_url) return;
    let cancelled = false;
    (async () => {
      const g = Math.max(4, Math.min(256, Number(loadSprite.width || loadSprite.height) || gridRef.current));
      sourceUrlRef.current = loadSprite.data_url;
      dirtyRef.current = false;
      const cells = await applyCells(loadSprite.data_url, g);
      if (cancelled) return;
      setGrid(g);
      setPixels(cells);
      setName(String(loadSprite.name || "sprite").replace(/\.[^.]+$/, ""));
      setEditId(loadSprite.asCopy ? null : (loadSprite.id || null));
      if (loadSprite.category) setKind(loadSprite.category);
      if (loadSprite.subtype) setSubtype(loadSprite.subtype);
      setSel(null);
      setMask(null);
      onLoaded?.();
      toast.success(loadSprite.asCopy ? "Copia en el lienzo" : (loadSprite.id ? "Sprite cargado para editar" : "Imagen lista para recortar o pintar"));
    })().catch(() => toast.error("No se pudo cargar la imagen"));
    return () => { cancelled = true; };
  }, [loadSprite]); // eslint-disable-line

  const allowed = (x, y) => {
    if (mask && mask.size) return mask.has(y * grid + x);
    if (sel) return x >= sel.x && y >= sel.y && x < sel.x + sel.w && y < sel.y + sel.h;
    return true;
  };

  const xy = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const x = Math.floor((e.clientX - rect.left) / (rect.width / grid));
    const y = Math.floor((e.clientY - rect.top) / (rect.height / grid));
    if (x < 0 || y < 0 || x >= grid || y >= grid) return null;
    return { x, y, i: y * grid + x };
  };

  const preview = () => {
    if (!drag || !hover) return pixels;
    if (tool === "line") return paintPtsGrid(pixels, grid, bresenham(drag.x, drag.y, hover.x, hover.y), color);
    if (tool === "rect") return paintPtsGrid(pixels, grid, rectPts(drag.x, drag.y, hover.x, hover.y, fillShape), color);
    if (tool === "roundrect") return paintPtsGrid(pixels, grid, roundRectPts(drag.x, drag.y, hover.x, hover.y, fillShape), color);
    if (tool === "ellipse") return paintPtsGrid(pixels, grid, ellipsePts(drag.x, drag.y, hover.x, hover.y, fillShape), color);
    return pixels;
  };

  const draw = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.width = grid * cell;
    c.height = grid * cell;
    const ctx = c.getContext("2d");
    const view = preview();
    for (let y = 0; y < grid; y++) {
      for (let x = 0; x < grid; x++) {
        const p = view[y * grid + x];
        ctx.fillStyle = p || ((x + y) % 2 === 0 ? "#111114" : "#0c0c10");
        ctx.fillRect(x * cell, y * cell, cell, cell);
      }
    }
    ctx.strokeStyle = "rgba(255,255,255,0.05)";
    for (let i = 0; i <= grid; i++) {
      ctx.beginPath(); ctx.moveTo(i * cell + 0.5, 0); ctx.lineTo(i * cell + 0.5, grid * cell); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, i * cell + 0.5); ctx.lineTo(grid * cell, i * cell + 0.5); ctx.stroke();
    }
    if (sel) {
      ctx.strokeStyle = "rgba(56,189,248,0.95)";
      ctx.setLineDash([4, 3]);
      ctx.strokeRect(sel.x * cell + 0.5, sel.y * cell + 0.5, sel.w * cell, sel.h * cell);
      ctx.setLineDash([]);
    }
    if (mask && mask.size) {
      ctx.fillStyle = "rgba(56,189,248,0.18)";
      mask.forEach((i) => {
        const x = i % grid, y = Math.floor(i / grid);
        ctx.fillRect(x * cell, y * cell, cell, cell);
      });
    }
    if (cloneSrc) {
      ctx.strokeStyle = "rgba(250,204,21,0.9)";
      ctx.strokeRect(cloneSrc.x * cell + 0.5, cloneSrc.y * cell + 0.5, cell, cell);
    }
  }, [pixels, grid, cell, drag, hover, tool, color, fillShape, sel, mask, cloneSrc]); // eslint-disable-line

  useEffect(() => { draw(); }, [draw]);

  const onDown = (e) => {
    const p = xy(e);
    if (!p) return;
    if (e.button === 2 || tool === "picker") {
      if (pixels[p.i]) (e.altKey || e.button === 2 ? setColorB : setColor)(pixels[p.i]);
      e.preventDefault();
      return;
    }
    if (tool === "clone" && (e.altKey || !cloneSrc)) {
      setCloneSrc({ x: p.x, y: p.y });
      cloneAnchor.current = null;
      toast.message("Origen de clon (Alt+clic para cambiar)");
      return;
    }
    if (tool === "text") {
      const txt = window.prompt("Texto a sellar", "A");
      if (!txt) return;
      pushHist(pixels);
      const off = document.createElement("canvas");
      off.width = grid;
      off.height = grid;
      const ctx = off.getContext("2d");
      ctx.imageSmoothingEnabled = false;
      ctx.font = `${Math.max(5, Math.floor(grid / 5))}px monospace`;
      ctx.fillStyle = color;
      ctx.textBaseline = "top";
      ctx.fillText(txt.slice(0, 12), p.x, p.y);
      const { data } = ctx.getImageData(0, 0, grid, grid);
      const next = [...pixels];
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] > 80) {
          const idx = i / 4;
          const x = idx % grid, y = Math.floor(idx / grid);
          if (allowed(x, y)) next[idx] = color;
        }
      }
      setPixels(next);
      return;
    }
    if (tool === "wand") {
      const idx = floodIdx(pixels, grid, p.i, tolerance);
      setMask(new Set(idx));
      setSel(boundsOf(idx, grid));
      return;
    }
    setDrawing(true);
    setDrag(p);
    strokeBase.current = pixels;
    cloneAnchor.current = p;
    if (["line", "rect", "roundrect", "ellipse", "select", "gradient", "move"].includes(tool)) return;
    pushHist(pixels);
    const next = [...pixels];
    if (tool === "bucket") setPixels(floodFill(pixels, grid, p.i, e.altKey ? colorB : color, tolerance));
    else if (tool === "recolor") {
      const target = pixels[p.i];
      setPixels(pixels.map((c, i) => {
        const x = i % grid, y = Math.floor(i / grid);
        return allowed(x, y) && colorDist(c, target) <= tolerance * 12 ? color : c;
      }));
    } else if (tool === "eraser") {
      if (allowed(p.x, p.y)) stampAt(next, grid, p.x, p.y, color, brush, true);
      setPixels(next);
    } else if (tool === "clone" && cloneSrc) {
      if (allowed(p.x, p.y)) next[p.i] = pixels[cloneSrc.y * grid + cloneSrc.x];
      setPixels(next);
    } else if (tool === "tone") {
      if (allowed(p.x, p.y) && next[p.i]) next[p.i] = toneHex(next[p.i], !e.altKey);
      setPixels(next);
    } else if (allowed(p.x, p.y)) {
      stampAt(next, grid, p.x, p.y, e.altKey ? colorB : color, tool === "pencil" ? 1 : brush);
      setPixels(next);
    }
  };

  const onMove = (e) => {
    const p = xy(e);
    setHover(p);
    if (!drawing || !p) return;
    if (["line", "rect", "roundrect", "ellipse", "select", "gradient", "move"].includes(tool)) return;
    const base = strokeBase.current || pixels;
    const next = [...pixels];
    if (tool === "eraser") {
      if (allowed(p.x, p.y)) stampAt(next, grid, p.x, p.y, color, brush, true);
    } else if (tool === "clone" && cloneSrc && cloneAnchor.current) {
      const ox = cloneSrc.x + (p.x - cloneAnchor.current.x);
      const oy = cloneSrc.y + (p.y - cloneAnchor.current.y);
      if (allowed(p.x, p.y) && ox >= 0 && oy >= 0 && ox < grid && oy < grid) next[p.i] = base[oy * grid + ox];
    } else if (tool === "tone") {
      if (allowed(p.x, p.y) && next[p.i]) next[p.i] = toneHex(next[p.i], !e.altKey);
    } else if (tool === "pencil" || tool === "brush") {
      if (allowed(p.x, p.y)) stampAt(next, grid, p.x, p.y, e.altKey ? colorB : color, tool === "pencil" ? 1 : brush);
    } else return;
    setPixels(next);
  };

  const finishStroke = (p) => {
    setDrawing(false);
    if (!drag) return;
    if (tool === "select" && p) {
      setSel({
        x: Math.min(drag.x, p.x), y: Math.min(drag.y, p.y),
        w: Math.abs(p.x - drag.x) + 1, h: Math.abs(p.y - drag.y) + 1,
      });
      setMask(null);
    } else if (tool === "move" && p && sel) {
      pushHist(pixels);
      const dx = p.x - drag.x, dy = p.y - drag.y;
      const chunk = [];
      for (let y = 0; y < sel.h; y++) for (let x = 0; x < sel.w; x++) chunk.push(pixels[(sel.y + y) * grid + (sel.x + x)]);
      const next = [...pixels];
      for (let y = 0; y < sel.h; y++) for (let x = 0; x < sel.w; x++) next[(sel.y + y) * grid + (sel.x + x)] = null;
      for (let y = 0; y < sel.h; y++) for (let x = 0; x < sel.w; x++) {
        const nx = sel.x + x + dx, ny = sel.y + y + dy;
        if (nx >= 0 && ny >= 0 && nx < grid && ny < grid) next[ny * grid + nx] = chunk[y * sel.w + x];
      }
      setPixels(next);
      setSel({ ...sel, x: sel.x + dx, y: sel.y + dy });
    } else if (tool === "gradient" && p) {
      pushHist(pixels);
      const dx = p.x - drag.x, dy = p.y - drag.y;
      const len2 = dx * dx + dy * dy || 1;
      const next = [...pixels];
      for (let y = 0; y < grid; y++) {
        for (let x = 0; x < grid; x++) {
          if (!allowed(x, y)) continue;
          const tt = Math.max(0, Math.min(1, ((x - drag.x) * dx + (y - drag.y) * dy) / len2));
          next[y * grid + x] = lerpHex(color, colorB, tt);
        }
      }
      setPixels(next);
    } else if (["line", "rect", "roundrect", "ellipse"].includes(tool) && p) {
      pushHist(pixels);
      const pts = tool === "line"
        ? bresenham(drag.x, drag.y, p.x, p.y)
        : tool === "rect" ? rectPts(drag.x, drag.y, p.x, p.y, fillShape)
          : tool === "roundrect" ? roundRectPts(drag.x, drag.y, p.x, p.y, fillShape)
            : ellipsePts(drag.x, drag.y, p.x, p.y, fillShape);
      setPixels(paintPtsGrid(pixels, grid, pts.filter(([x, y]) => allowed(x, y)), color));
    }
    setDrag(null);
    strokeBase.current = null;
  };

  const onUp = (e) => finishStroke(xy(e) || hover);

  const undo = () => {
    setHistory((h) => {
      if (!h.length) return h;
      setFuture((f) => [pixelsRef.current, ...f]);
      setPixels(h[h.length - 1]);
      return h.slice(0, -1);
    });
  };
  const redo = () => {
    setFuture((f) => {
      if (!f.length) return f;
      setHistory((h) => [...h, pixelsRef.current]);
      setPixels(f[0]);
      return f.slice(1);
    });
  };

  const transform = (fn) => {
    pushHist(pixels);
    const next = emptyGrid(grid);
    for (let y = 0; y < grid; y++) for (let x = 0; x < grid; x++) {
      const [nx, ny] = fn(x, y);
      next[ny * grid + nx] = pixels[y * grid + x];
    }
    setPixels(next);
  };

  const cropSel = () => {
    if (!sel) return toast.message("Dibuja una selección primero");
    pushHist(pixels);
    const n = Math.max(sel.w, sel.h);
    const next = emptyGrid(n);
    for (let y = 0; y < sel.h; y++) for (let x = 0; x < sel.w; x++) next[y * n + x] = pixels[(sel.y + y) * grid + (sel.x + x)];
    sourceUrlRef.current = null;
    dirtyRef.current = true;
    setPixels(next);
    setGrid(n);
    setSel(null);
    setMask(null);
  };

  const copySel = () => {
    if (!sel) return toast.message("Selecciona un área");
    const data = [];
    for (let y = 0; y < sel.h; y++) for (let x = 0; x < sel.w; x++) data.push(pixels[(sel.y + y) * grid + (sel.x + x)]);
    clipRef.current = { w: sel.w, h: sel.h, data };
    toast.success("Copiado");
  };
  const cutSel = () => {
    if (!sel) return;
    copySel();
    pushHist(pixels);
    const next = [...pixels];
    for (let y = 0; y < sel.h; y++) for (let x = 0; x < sel.w; x++) next[(sel.y + y) * grid + (sel.x + x)] = null;
    setPixels(next);
  };
  const pasteSel = () => {
    const clip = clipRef.current;
    if (!clip) return toast.message("Nada en el portapapeles");
    pushHist(pixels);
    const ox = sel?.x || 0, oy = sel?.y || 0;
    const next = [...pixels];
    for (let y = 0; y < clip.h; y++) for (let x = 0; x < clip.w; x++) {
      const nx = ox + x, ny = oy + y;
      if (nx >= 0 && ny >= 0 && nx < grid && ny < grid) next[ny * grid + nx] = clip.data[y * clip.w + x];
    }
    setSel({ x: ox, y: oy, w: clip.w, h: clip.h });
    setPixels(next);
  };
  const clearSel = () => {
    if (!sel && !(mask && mask.size)) {
      pushHist(pixels);
      setPixels(emptyGrid(grid));
      return;
    }
    pushHist(pixels);
    const next = [...pixels];
    if (mask && mask.size) mask.forEach((i) => { next[i] = null; });
    else if (sel) {
      for (let y = 0; y < sel.h; y++) for (let x = 0; x < sel.w; x++) next[(sel.y + y) * grid + (sel.x + x)] = null;
    }
    setPixels(next);
  };

  const buildPng = () => {
    const off = document.createElement("canvas");
    off.width = grid; off.height = grid;
    const ctx = off.getContext("2d");
    for (let y = 0; y < grid; y++) for (let x = 0; x < grid; x++) {
      const c = pixels[y * grid + x];
      if (c) { ctx.fillStyle = c; ctx.fillRect(x, y, 1, 1); }
    }
    return off.toDataURL("image/png");
  };

  const handleSave = async (asCopy = false) => {
    try {
      const body = {
        name: assetFileName(kind, name, grid),
        data_url: buildPng(),
        category: kind,
        subtype: subtype || kind,
        tags: [kind, subtype].filter(Boolean),
        studio: studio?.id,
        width: grid,
        height: grid,
      };
      if (editId && !asCopy) {
        try {
          await projectsApi.updateSprite(project.project_id, editId, body);
        } catch {
          await projectsApi.addSprite(project.project_id, body);
        }
        toast.success("Sprite actualizado");
      } else {
        const saved = await projectsApi.addSprite(project.project_id, body);
        setEditId(saved?.id || null);
        toast.success(t("ide.saved"));
      }
      sourceUrlRef.current = body.data_url;
      dirtyRef.current = false;
      const p = await projectsApi.get(project.project_id);
      onChange({ sprites: p.sprites });
    } catch {
      toast.error(t("toast.saveFailed"));
    }
  };

  const handleAI = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    try {
      const res = await ai.sprite({
        prompt: `${studio?.ai || "pixel art sprite"}. Camera: ${camera}. Type: ${kind}. Size ${grid}x${grid}. ${aiPrompt}`,
        style: studio?.ai || "pixel art",
        size: grid,
      });
      const raw = res.data || "";
      const url = raw.startsWith("data:") ? raw : `data:${res.mime_type || "image/png"};base64,${raw}`;
      pushHist(pixels);
      sourceUrlRef.current = url;
      dirtyRef.current = false;
      setPixels(await applyCells(url, grid));
      toast.success(`Sprite ${grid}×${grid} en el lienzo`);
    } catch (e) {
      pushHist(pixels);
      try {
        setPixels(await applyCells(proceduralSprite(`${kind} ${aiPrompt}`, grid), grid));
      } catch { /* ignore */ }
      toast.error(e?.response?.data?.detail || t("toast.aiFailed"));
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.closest?.("input, textarea, select")) return;
      const k = e.key.toLowerCase();
      if ((e.ctrlKey || e.metaKey) && k === "z") { e.preventDefault(); e.shiftKey ? redo() : undo(); return; }
      if ((e.ctrlKey || e.metaKey) && k === "y") { e.preventDefault(); redo(); return; }
      if ((e.ctrlKey || e.metaKey) && k === "c") { e.preventDefault(); copySel(); return; }
      if ((e.ctrlKey || e.metaKey) && k === "x") { e.preventDefault(); cutSel(); return; }
      if ((e.ctrlKey || e.metaKey) && k === "v") { e.preventDefault(); pasteSel(); return; }
      if ((e.ctrlKey || e.metaKey) && k === "s") { e.preventDefault(); handleSave(false); return; }
      if (k === "x" && !e.ctrlKey) { setColor((c) => { setColorB(c); return colorB; }); return; }
      if (k === "delete" || k === "backspace") { e.preventDefault(); clearSel(); return; }
      const map = { p: "pencil", b: "brush", e: "eraser", g: "bucket", i: "picker", n: "line", u: "rect", o: "ellipse", m: "select", w: "wand", h: "gradient", s: "clone", t: "text" };
      if (!e.ctrlKey && map[k]) setTool(map[k]);
      if (k === "[") setBrush((n) => Math.max(1, n - 1));
      if (k === "]") setBrush((n) => Math.min(8, n + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const btn = (on) => "w-8 h-8 flex items-center justify-center " + (on ? "bg-cyan-400 text-zinc-950" : "bg-zinc-950 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900");

  return (
    <div className="p-4 flex flex-col xl:flex-row gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="flex flex-wrap gap-px border border-zinc-800">
            {TOOLS.map(({ id, icon: Icon, label }) => (
              <button key={id} type="button" title={label} onClick={() => setTool(id)} className={btn(tool === id)}>
                <Icon size={15} weight={tool === id ? "fill" : "regular"} />
              </button>
            ))}
          </div>
          <div className="flex gap-px border border-zinc-800">
            <button type="button" title="Deshacer" disabled={!history.length} onClick={undo} className={btn(false)}><ArrowUUpLeft size={15} /></button>
            <button type="button" title="Rehacer" disabled={!future.length} onClick={redo} className={btn(false)}><ArrowUUpRight size={15} /></button>
            <button type="button" title="Intercambiar colores (X)" onClick={() => { setColor(colorB); setColorB(color); }} className={btn(false)}><ArrowsLeftRight size={15} /></button>
            <button type="button" title="Voltear H" onClick={() => transform((x, y) => [grid - 1 - x, y])} className={btn(false)}><FlipHorizontal size={15} /></button>
            <button type="button" title="Voltear V" onClick={() => transform((x, y) => [x, grid - 1 - y])} className={btn(false)}><FlipVertical size={15} /></button>
            <button type="button" title="Rotar 90°" onClick={() => transform((x, y) => [grid - 1 - y, x])} className={btn(false)}><ArrowClockwise size={15} /></button>
            <button type="button" title="Rotar -90°" onClick={() => transform((x, y) => [y, grid - 1 - x])} className={btn(false)}><ArrowCounterClockwise size={15} /></button>
            <button type="button" title="Recortar selección" onClick={cropSel} className={btn(false)}><Crop size={15} /></button>
            <button type="button" title="Copiar selección" onClick={copySel} className={btn(false)}><Copy size={15} /></button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3 text-[11px]">
          <span className="uppercase tracking-wider text-zinc-500">Lienzo</span>
          {STANDARD_PIXEL_SIZES.map((s) => (
            <button key={s} type="button" onClick={() => setGridKeep(s)} className={"px-2 py-1 font-mono " + (grid === s ? "bg-cyan-400 text-zinc-950" : "bg-zinc-950 text-zinc-300 border border-zinc-800")}>{s}</button>
          ))}
          <input
            className="w-16 bg-zinc-950 border border-zinc-800 px-2 py-1 font-mono text-zinc-200"
            placeholder="otro"
            value={customSize}
            onChange={(e) => setCustomSize(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { setGridKeep(customSize); setCustomSize(""); } }}
            title="Tamaño personalizado y Enter"
          />
          <button type="button" className="px-2 py-1 bg-zinc-950 border border-zinc-800 text-zinc-300" onClick={() => { if (customSize) { setGridKeep(customSize); setCustomSize(""); } }}>Aplicar</button>
          <span className="text-zinc-500">{grid}×{grid}</span>
          <select className="bg-zinc-950 border border-zinc-800 px-1 py-1 text-zinc-300" value={scaleMode} onChange={(e) => setScaleMode(e.target.value)}>
            <option value="scale">Reescalar</option>
            <option value="pad">Lienzo (pad/crop)</option>
          </select>
          <span className="uppercase tracking-wider text-zinc-500 ml-2">Zoom</span>
          <select className="bg-zinc-950 border border-zinc-800 px-1 py-1 text-zinc-200" value={cell} onChange={(e) => setCell(+e.target.value)}>
            {CELL_SIZES.map((s) => <option key={s} value={s}>{s}px</option>)}
          </select>
          <span className="uppercase tracking-wider text-zinc-500 ml-2">Pincel</span>
          <input type="range" min={1} max={8} value={brush} onChange={(e) => setBrush(+e.target.value)} />
          <span className="font-mono text-zinc-400">{brush}</span>
          <label className="flex items-center gap-1 text-zinc-400">
            <input type="checkbox" checked={fillShape} onChange={(e) => setFillShape(e.target.checked)} /> Relleno
          </label>
          <span className="text-zinc-500">Tolerancia</span>
          <input type="range" min={0} max={8} value={tolerance} onChange={(e) => setTolerance(+e.target.value)} />
        </div>

        <div className="bg-zinc-900 border border-zinc-800 p-3 inline-block max-w-full overflow-auto">
          <canvas
            ref={canvasRef}
            onMouseDown={onDown}
            onMouseMove={onMove}
            onMouseUp={onUp}
            onContextMenu={(e) => e.preventDefault()}
            onMouseLeave={() => finishStroke(hover)}
            className="cursor-crosshair block"
            style={{ imageRendering: "pixelated" }}
          />
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {PALETTE.map((c) => (
            <button key={c} type="button" onClick={() => setColor(c)} onContextMenu={(e) => { e.preventDefault(); setColorB(c); }} className={"w-6 h-6 " + (color === c ? "ring-2 ring-cyan-400 z-10" : "")} style={{ backgroundColor: c }} />
          ))}
          {lockedPalette.map((c) => (
            <button key={`p-${c}`} type="button" onClick={() => setColor(c)} className={"w-6 h-6 " + (color === c ? "ring-2 ring-cyan-400" : "")} style={{ backgroundColor: c }} />
          ))}
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-8 h-8 bg-transparent border border-zinc-800" title="Primario" />
          <input type="color" value={colorB} onChange={(e) => setColorB(e.target.value)} className="w-8 h-8 bg-transparent border border-zinc-800" title="Secundario" />
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="bg-zinc-950 border border-zinc-800 text-xs font-mono px-2 py-1.5 text-zinc-200">
            {kinds.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
          </select>
          {subtypes.length > 0 && (
            <select value={subtype} onChange={(e) => setSubtype(e.target.value)} className="bg-zinc-950 border border-zinc-800 text-xs font-mono px-2 py-1.5 text-zinc-200">
              {subtypes.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
          )}
          <input value={name} onChange={(e) => setName(e.target.value)} className="bg-zinc-900 border border-zinc-800 text-xs font-mono w-44 px-2 py-1.5 text-zinc-100" placeholder="nombre" />
          <button type="button" onClick={() => handleSave(false)} className="gc-btn gc-btn-primary h-9 px-3 text-xs"><FloppyDisk size={14} /> {editId ? "Guardar cambios" : "Guardar"}</button>
          <button type="button" onClick={() => handleSave(true)} className="gc-btn gc-btn-ghost h-9 px-3 text-xs"><Copy size={14} /> Guardar copia</button>
          <button type="button" onClick={() => { const a = document.createElement("a"); a.href = buildPng(); a.download = `${name || "sprite"}.png`; a.click(); }} className="gc-btn gc-btn-ghost h-9 px-3 text-xs"><Download size={14} /> PNG</button>
          <button type="button" onClick={() => { pushHist(pixels); setPixels(emptyGrid(grid)); setEditId(null); sourceUrlRef.current = null; }} className="gc-btn gc-btn-ghost h-9 px-3 text-xs text-red-400"><Trash size={14} /> Limpiar</button>
          <button type="button" onClick={() => { setEditId(null); setName("sprite-1"); setPixels(emptyGrid(grid)); sourceUrlRef.current = null; dirtyRef.current = false; }} className="gc-btn gc-btn-ghost h-9 px-3 text-xs"><Plus size={14} /> Nuevo</button>
        </div>
        {editId && <div className="text-[10px] mt-1 text-cyan-400">Editando un sprite guardado. “Guardar copia” crea uno nuevo.</div>}
      </div>

      <div className="xl:w-72 shrink-0">
        <div className="text-[10px] tracking-[0.3em] uppercase text-cyan-400 mb-2 flex items-center gap-2"><Sparkle size={14} weight="fill" /> IA</div>
        <input value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} placeholder={t("sprite.ai.placeholder")} className="bg-zinc-900 border border-zinc-800 font-mono text-sm w-full px-2 py-2 mb-2 text-zinc-100" />
        <button type="button" onClick={handleAI} disabled={aiLoading || !aiPrompt.trim()} className="gc-btn gc-btn-primary h-9 w-full text-xs">
          <Sparkle size={14} weight="fill" /> {aiLoading ? `Pintando ${grid}×${grid}…` : t("sprite.ai.generate")}
        </button>
        <p className="mt-4 text-[11px] leading-relaxed text-zinc-500">
          {studio?.label || "Sprites"} · {grid}×{grid} · zoom {cell}px. Lápiz, pincel, goma, bote, recolor, cuentagotas, línea, formas, selección, varita, mover, degradado, clonar, texto y tono. El lateral muestra lo subido.
        </p>
      </div>
    </div>
  );
}
